import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { AlertCircle, Bookmark, Search } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { SocialFollow } from '@/components/ui/social-follow';
import logoFull from '@/assets/odyvoy-logo-full.png';
import { BlogSearchBar } from '@/components/blog/BlogSearchBar';
import { PricingHeader } from '@/components/pricing/PricingHeader';
import { NewsletterSignup } from '@/components/landing/NewsletterSignup';
import { useBlogPosts, type BlogPost } from '@/hooks/useBlogPosts';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import '@/styles/blog.css';

const Blog = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const { data: blogPosts = [], isLoading, error } = useBlogPosts();

  const handlePostClick = (post: BlogPost) => {
    const slug = post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    navigate(`/blog/${slug}`);
  };

  // Handle opening article from saved articles page
  useEffect(() => {
    if (location.state?.openArticleId && blogPosts.length > 0) {
      const post = blogPosts.find(p => p.id === location.state.openArticleId);
      if (post) {
        handlePostClick(post);
      }
    }
  }, [location.state, blogPosts]);

  const categories = ['All', ...Array.from(new Set(blogPosts.map(post => post.category)))];

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });


  return (
    <div className="journal-page min-h-screen bg-background text-foreground">
      <Helmet>
        <title>The Journal | OdyVoy — Travel Tips & Destination Guides</title>
        <meta name="description" content="Expert travel tips, packing guides, and destination insights. Pack smart, travel on a budget, and explore the world with OdyVoy." />
        <meta name="keywords" content="travel blog, travel tips, packing guide, destination guide, travel inspiration, budget travel, smart packing, travel advice" />
        <link rel="canonical" href="https://odyvoy.com/blog" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="The Journal | OdyVoy — Travel Tips & Guides" />
        <meta property="og:description" content="Expert travel tips, destination guides, and travel inspiration to make your next adventure unforgettable." />
        <meta property="og:url" content="https://odyvoy.com/blog" />
        <meta property="og:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Journal | OdyVoy — Travel Tips & Guides" />
        <meta name="twitter:description" content="Expert travel tips, destination guides, and travel inspiration." />
        <meta name="twitter:image" content="https://odyvoy.com/lovable-uploads/odyvoy-logo.png" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "OdyVoy Travel Blog",
            "description": "Expert travel tips, destination guides, and travel inspiration",
            "url": "https://odyvoy.com/blog",
            "publisher": {
              "@type": "Organization",
              "name": "OdyVoy",
              "logo": {
                "@type": "ImageObject",
                "url": "https://odyvoy.com/lovable-uploads/odyvoy-logo.png"
              }
            },
            "blogPost": blogPosts.slice(0, 10).map(post => {
              const slug = post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              return {
                "@type": "BlogPosting",
                "headline": post.title,
                "description": post.excerpt,
                "url": `https://odyvoy.com/blog/${slug}`,
                "author": {
                  "@type": "Person",
                  "name": post.author
                },
                "datePublished": post.date,
                "image": post.image.startsWith('http') ? post.image : `https://odyvoy.com${post.image}`,
                "articleSection": post.category,
                "keywords": post.tags.join(', ')
              };
            })
          })}
        </script>
      </Helmet>

      <PricingHeader showSectionLinks={false} />
      <main className="journal-container">
        <div className="journal-intro">
          <h1>The&nbsp;<span className="text-primary">Journal</span></h1>
          <div className="journal-intro-rule" aria-hidden="true" />
          <div className="journal-brand" aria-hidden="true">
            <img src="/lovable-uploads/odyvoy-icon.png" alt="" width={104} height={104} />
            <div>
              <p className="journal-brand-name">OdyVoy</p>
              <p className="journal-brand-tagline">Your Odyssey. Our Voyage!</p>
            </div>
          </div>
          <div className="journal-intro-row">
            <p>Practical guides on packing, visas, money and health abroad, written to help you check everything before you leave.</p>
            <Link to="/saved-articles" className="journal-saved"><Bookmark className="h-4 w-4" />Saved articles</Link>
          </div>
          <div className="journal-search">
            <BlogSearchBar value={searchQuery} onChange={setSearchQuery} posts={blogPosts} onPostSelect={handlePostClick} />
          </div>
        </div>
        <nav className="journal-categories" aria-label="Article categories">
          {categories.map(category => (
            <Button key={category} variant="ghost" className="journal-category" aria-pressed={selectedCategory === category} onClick={() => setSelectedCategory(category)}>
              {category === 'All' ? 'All stories' : category}
            </Button>
          ))}
        </nav>
        {isLoading && <div className="grid grid-cols-1 gap-12 sm:grid-cols-2 lg:grid-cols-3" aria-label="Loading articles">{[1, 2, 3].map(i => <div key={i}><Skeleton className="aspect-[4/5] w-full" /><Skeleton className="mt-6 h-6 w-3/4" /></div>)}</div>}
        {error && <Alert variant="destructive"><AlertCircle className="h-4 w-4" /><AlertDescription>Failed to load blog posts. Please try again later.</AlertDescription></Alert>}
        {!isLoading && !error && (filteredPosts.length > 0 ? (
          <div className="journal-columns" aria-label="Journal articles">
            {[...filteredPosts.filter(post => post.featured), ...filteredPosts.filter(post => !post.featured)].map((post, index) => {
              const slug = post.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
              return <article key={post.id} className="journal-story">
                <Link to={`/blog/${slug}`}>
                  <img src={post.image} alt={post.title} className="journal-story-image" loading={index < 3 ? 'eager' : 'lazy'} decoding="async" width={600} height={750} />
                  <div className="journal-story-meta"><strong>{post.category}</strong><span>{post.readTime}</span></div>
                  <h2>{post.title}</h2>
                  {post.excerpt && <p>{post.excerpt}</p>}
                  <time dateTime={post.date}>{new Date(post.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</time>
                </Link>
              </article>;
            })}
          </div>
        ) : <div className="journal-empty" role="status"><Search className="mx-auto mb-4 h-8 w-8" /><p>No articles found matching your search.</p><Button variant="outline" className="mt-4 min-h-12" onClick={() => { setSearchQuery(''); setSelectedCategory('All'); }}>Clear filters</Button></div>)}
      </main>
      <NewsletterSignup showBlogLink={false} />
      <footer className="journal-footer">
        <div className="journal-container journal-footer-inner">
          <div className="journal-footer-brand">
            <Link to="/"><img src={logoFull} alt="OdyVoy - Your Odyssey. Our Voyage!" width={1259} height={292} loading="lazy" decoding="async" /></Link>
            <SocialFollow className="journal-footer-social" />
          </div>
          <p>© 2026 OdyVoy. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Blog;
